import { Dropdown, NumberInput, Layer } from '@carbon/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { handleIntegerKeyPress, parseNumberAndSet } from '../utils';
import { getError } from 'atoms/form-atoms';
import style from './forecast-settings.module.scss';
import { DiFormat, QiFormat, ForecastDurationOption } from 'types/api-payloads';
import {
  forecastSettingsFormAtom,
  oilModelFormAtom,
  gasModelFormAtom,
  waterModelFormAtom,
  oilModelFormValuesAtom,
  gasModelFormValuesAtom,
  waterModelFormValuesAtom,
} from '../atoms';

export function ForecastSettings() {
  const forecastSettingsAtom = useAtomValue(forecastSettingsFormAtom);
  const [forecastDurationField, setForecastDurationField] = useAtom(
    forecastSettingsAtom.forecastDuration
  );
  const [qiFormatField, setQiFormatField] = useAtom(
    forecastSettingsAtom.qiFormat
  );
  const [diFormatField, setDiFormatField] = useAtom(
    forecastSettingsAtom.diFormat
  );
  const [forecastDurationOptionField, setForecastDurationOptionField] = useAtom(
    forecastSettingsAtom.forecastDurationOption
  );

  // Get form atoms to trigger recreation when diFormat changes
  const setOilForm = useSetAtom(oilModelFormAtom);
  const setGasForm = useSetAtom(gasModelFormAtom);
  const setWaterForm = useSetAtom(waterModelFormAtom);

  const oilValues = useAtomValue(oilModelFormValuesAtom);
  const gasValues = useAtomValue(gasModelFormValuesAtom);
  const waterValues = useAtomValue(waterModelFormValuesAtom);

  const qiFormatItems: Array<{ id: QiFormat; label: string }> = [
    { id: 'months', label: 'Monthly' },
    { id: 'days', label: 'Daily' },
  ];

  const diFormatItems: Array<{ id: DiFormat; label: string }> = [
    { id: 'effective', label: 'Effective' },
    { id: 'nominal', label: 'Nominal' },
  ];

  const selectedQiFormatItem =
    qiFormatItems.find((item) => item.id === qiFormatField.value) ||
    qiFormatItems[0];
  const selectedDiFormatItem =
    diFormatItems.find((item) => item.id === diFormatField.value) ||
    diFormatItems[0];

  return (
    <div className={style.forecastSettingsWrapper}>
      <Layer>
        <NumberInput
          id="forecast-duration-years"
          label="Forecast Duration Years"
          min={1}
          max={100}
          step={1}
          hideSteppers={true}
          value={forecastDurationField.value ?? ''}
          placeholder="30"
          allowEmpty={true}
          inputMode="numeric"
          size="md"
          invalid={
            !forecastDurationField.isValid && forecastDurationField.isDirty
          }
          invalidText={
            !forecastDurationField.isValid && forecastDurationField.isDirty
              ? getError(forecastDurationField) || ''
              : ''
          }
          onKeyDown={handleIntegerKeyPress}
          // TODO: this onChange gives us an event, while the other ones give custom objects, WHY
          onChange={(event: any) =>
            parseNumberAndSet(event.target.value, setForecastDurationField)
          }
        />
      </Layer>
      <div className={style.forecastDurationOptions}>
        <Layer>
          <Dropdown
            id="dca-duration-options"
            items={['production_start', 'production_end', 'forecast_start']}
            itemToString={(item) => {
              return item ? `From ${item.replace('_', ' ')}` : '';
            }}
            label="Forecast Duration Options"
            titleText="Forecast Duration Options"
            selectedItem={forecastDurationOptionField.value}
            onChange={({ selectedItem }) => {
              if (selectedItem) {
                setForecastDurationOptionField(
                  selectedItem as ForecastDurationOption
                );
              }
            }}
          ></Dropdown>
        </Layer>
      </div>
      <div className={style.forecastSettingsContainer}>
        <div className={style.forecastSettingControl}>
          <Layer>
            <Dropdown
              id="qi-format-dropdown"
              titleText="qi format"
              label={selectedQiFormatItem.label}
              items={qiFormatItems.map((item) => ({
                id: item.id,
                label: item.label,
              }))}
              selectedItem={selectedQiFormatItem}
              itemToString={(item) => item?.label ?? ''}
              onChange={({ selectedItem }) => {
                if (selectedItem) {
                  setQiFormatField(selectedItem.id);
                }
              }}
            />
          </Layer>
        </div>
        <div className={style.forecastSettingControl}>
          <Layer>
            <Dropdown
              id="di-format-dropdown"
              titleText="Di format"
              label={selectedDiFormatItem.label}
              items={diFormatItems}
              selectedItem={selectedDiFormatItem}
              itemToString={(item) => item?.label ?? ''}
              onChange={({ selectedItem }) => {
                if (selectedItem) {
                  setDiFormatField(selectedItem.id);

                  // Trigger form recreation for all volume model forms with new diFormat
                  setOilForm({
                    declineModel: oilValues.declineModel.value,
                    forecastStartDate: oilValues.startDate.value,
                    qi: oilValues.qi.value,
                    di: oilValues.di.value,
                    bFactor: oilValues.bFactor?.value,
                    dlim: oilValues.dlim?.value,
                    rampupModel: oilValues.rampupModel.value,
                    qiRampup: oilValues.qiRampup?.value,
                    rampupDuration: oilValues.rampupDuration?.value,
                    n: oilValues.n?.value,
                    ri: oilValues.ri?.value ?? null,
                    tElf: oilValues.tElf?.value ?? null,
                    m: oilValues.m?.value ?? null,
                  });

                  setGasForm({
                    declineModel: gasValues.declineModel.value,
                    forecastStartDate: gasValues.startDate.value,
                    qi: gasValues.qi.value,
                    di: gasValues.di.value,
                    bFactor: gasValues.bFactor?.value,
                    dlim: gasValues.dlim?.value,
                    rampupModel: gasValues.rampupModel.value,
                    qiRampup: gasValues.qiRampup?.value,
                    rampupDuration: gasValues.rampupDuration?.value,
                    n: gasValues.n?.value,
                    ri: gasValues.ri?.value ?? null,
                    tElf: gasValues.tElf?.value ?? null,
                    m: gasValues.m?.value ?? null,
                  });

                  setWaterForm({
                    declineModel: waterValues.declineModel.value,
                    forecastStartDate: waterValues.startDate.value,
                    qi: waterValues.qi.value,
                    di: waterValues.di.value,
                    bFactor: waterValues.bFactor?.value,
                    dlim: waterValues.dlim?.value,
                    rampupModel: waterValues.rampupModel.value,
                    qiRampup: waterValues.qiRampup?.value,
                    rampupDuration: waterValues.rampupDuration?.value,
                    n: waterValues.n?.value,
                    ri: waterValues.ri?.value ?? null,
                    tElf: waterValues.tElf?.value ?? null,
                    m: waterValues.m?.value ?? null,
                  });
                }
              }}
            />
          </Layer>
        </div>
      </div>
    </div>
  );
}
